/**
 * Component for Gift card balance checker
 *
 * @module views/components/GiftCardBalanceChecker
 * @memberof -Common
 */
import './GiftCardBalanceChecker.scss';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import Button from '@ulta/core/components/Button/Button';
import GridContainer from '@ulta/core/components/GridContainer/GridContainer';
import Icon from '@ulta/core/components/Icon/Icon';
import InputField from '@ulta/core/components/InputField_New/InputField_New';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Text from '@ulta/core/components/Text/Text';
import { useIntersectionProcessor } from '@ulta/core/hooks/useIntersectionProcessor/useIntersectionProcessor';
import useLoader from '@ulta/core/hooks/useLoader/useLoader';
import { useNextRender } from '@ulta/core/hooks/useNextRender/useNextRender';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { hasItems } from '@ulta/core/utils/array/array';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';

import Markdown from '@ulta/modules/Markdown/Markdown';
import UltaAsset from '@ulta/modules/UltaAsset/UltaAsset';

import constants from '@ulta/utils/constants/constants';
import interpolateString from '@ulta/utils/interpolateString/interpolateString';
import { addYupCustomRegex, isRequiredValidation } from '@ulta/utils/Validations/Validations';

import BlockMessage from '../BlockMessage/BlockMessage';
import * as utils from './GiftCardBalanceChecker';


/**
 * Represents a GiftCardBalanceChecker component
 *
 * @method
 * @param {object} props - React properties passed from composition
 * @returns GiftCardBalanceChecker
*/
export const GiftCardBalanceChecker = function( props ){
  const {
    background,
    input,
    helpText,
    balance,
    messages,
    selectedView,
    invokeMutation,
    unavailable,
    loading
  } = props;

  const giftCardBalanceChecker = useRef( null );
  const { desktopImage, mobileImage } = handleEmptyObjects( background );
  const { headline, subHeadline, requiredText, numberInput, pinInput, checkBalanceAction } = handleEmptyObjects( input );
  const { headline: balanceHeadline, currentBalance, timestamp, giftCardTitle, giftCardIcon, cardNumber, checkAnotherCardAction } = handleEmptyObjects( balance );
  const { headline: unavailableHeadline, unavailableMessage, actionGroups } = handleEmptyObjects( unavailable );
  const validationSchema = utils.getValidationSchema( { numberInput, pinInput } );
  const { breakpoint } = useDeviceInflection();
  const formikRef = useRef();
  const balanceRef = useRef();
  const [giftCardView, setGiftCardView] = useState();
  const [formattedDate, setFormattedDate] = useState();
  const [formattedTime, setFormattedTime] = useState();
  const initialValues = {
    numberInput: '',
    pinInput: ''
  };
  const hasMessages = hasItems( messages );
  const backgroundImageProps = breakpoint.isSmallDevice() ? mobileImage : desktopImage;
  const [scheduleFocus] = useNextRender();
  const callGiftCardSupportAction = actionGroups?.actions[0];
  const continueShoppingAction = actionGroups?.actions[1];
  const [loader, showLoader, hideLoader] = useLoader();

  useIntersectionProcessor( {
    ...props,
    componentRef: giftCardBalanceChecker
  } );

  useEffect( () => {
    setGiftCardView( selectedView );
  }, [selectedView, balance] );

  /**
   * When the identity of balance is updated, we need to reset the formatted date/time.
   * In the scenario you are checking the same gift card twice, the identity of balance is udpated
   * on a successful dxl response, but all values in the object will remain the same.
   */
  useEffect( () => {
    if( selectedView === 'balance' ){
      const currentDate = new Date();
      setFormattedDate( currentDate.toLocaleString( 'en-US', { dateStyle: 'short' } ) );
      setFormattedTime( currentDate.toLocaleString( 'en-US', { timeStyle: 'short' } ) );
      scheduleFocus( () => {
        if( balanceRef.current ){
          balanceRef.current.focus();
        }
      } );
    }
  }, [balance] );

  /**
   * Form submit handler
   */
  const onSubmit = useCallback(
    composeOnSubmit(
      { action: checkBalanceAction }, { invokeMutation, showLoader }
    ),
    [checkBalanceAction]
  );

  useEffect( () => {
    utils.handleLoadingState( { loading }, { hideLoader } );
  }, [loading] );

  return (
    <div className={ `GiftCardBalanceChecker ${hasMessages ? 'has-message' : ''}` } ref={ giftCardBalanceChecker }>
      { loader }
      <div>
        { ( desktopImage && mobileImage ) &&
          <div className='GiftCardBalanceChecker__backgroundImage'>
            <UltaAsset
              { ...spreadProps( breakpoint.isSmallDevice() ? { ...mobileImage } : { ...desktopImage } ) }
              bgAnimation={ true }
              isBackground={ true }
            />
          </div>
        }
        <GridContainer>
          { giftCardView === 'input' &&
            <div className='GiftCardBalanceChecker__content'>
              <div className='GiftCardBalanceChecker__checkBalanceTitle'>
                <Text
                  htmlTag={ headline.htmlTag }
                  textStyle='title-xlg'
                  textAlign='center'
                >
                  { headline.text }
                </Text>
              </div>

              <div className='GiftCardBalanceChecker__subHeadline'>
                <Text
                  htmlTag='p'
                  textStyle='bodyDefault'
                  textAlign='center'
                >
                  { subHeadline }
                </Text>
              </div>

              <div className='GiftCardBalanceChecker__requiredText'>
                <Text
                  htmlTag='p'
                  textStyle='bodySm'
                >
                  { requiredText }
                </Text>
              </div>
              { hasMessages && (
                <div className='GiftCardBalanceChecker__blockErrorMessage'>
                  <ul className='GiftCardBalanceChecker__MessageList'>
                    { messages.map( ( message, idx ) => (
                      <li key={ `giftCardBalance-${idx}` } className='GiftCardBalanceChecker__Message'>
                        <BlockMessage
                          message={ message.message }
                          messageType={ message.icon }
                          backgroundColor={ message.backgroundColor }
                          borderColor={ message.borderColor }
                          textColor={ message.textColor }
                          showCloseButton={ false }
                        />
                      </li>
                    ) ) }
                  </ul>
                </div>
              ) }

              <Formik innerRef={ formikRef }
                initialValues={ initialValues }
                validationSchema={ validationSchema }
                onSubmit={ onSubmit }
              >
                { () =>
                  (
                    <Form className='GiftCardBalanceChecker__giftCardBalanceForm'>
                      { numberInput && (
                        <div className='GiftCardBalanceChecker__numberInput'>
                          <InputField
                            { ...spreadProps( { ...numberInput } ) }
                            id='numberInput'
                            formatter={ { pattern: constants.GIFTCARD.NUMBERFORMATTER } }
                          />
                        </div>
                      ) }
                      { pinInput && (
                        <div className='GiftCardBalanceChecker__pinInput'>
                          <InputField
                            { ...spreadProps( { ...pinInput } ) }
                            id='pinInput'
                            formatter={ { pattern: constants.GIFTCARD.PINFORMATTER } }
                            prefersRawValue={ true }
                          />
                        </div>
                      ) }
                      <div className='GiftCardBalanceChecker__checkBalanceAction'>
                        <Button
                          type='submit'
                          fullWidth={ true }
                          label={ checkBalanceAction.label }
                          ariaLabel={ checkBalanceAction.label }
                          action={ checkBalanceAction }
                        />
                      </div>
                    </Form>
                  ) }
              </Formik>
              <Markdown
                bodyStyle='body-2'
                cmsMarkdownContent={ helpText }
                alignment='center'
              />
            </div>
          }
          { giftCardView === 'balance' &&
            <div className='GiftCardBalanceChecker__content'>
              <div
                tabIndex='-1'
                ref={ balanceRef }
              >
                <div className='GiftCardBalanceChecker__cardBalanceTitle'>
                  <Text
                    htmlTag={ balanceHeadline.htmlTag }
                    textStyle='title-xlg'
                    textAlign='center'
                  >
                    { balanceHeadline.text }
                  </Text>
                </div>
                <div className='GiftCardBalanceChecker__currentBalance'>
                  <Text
                    htmlTag='p'
                    textStyle='displayXl'
                    textAlign='center'
                  >
                    { currentBalance }
                  </Text>
                </div>

                <div className='GiftCardBalanceChecker__timestamp'>
                  <Text
                    htmlTag='p'
                    textStyle='labelDefault'
                    textAlign='center'
                  >
                    { interpolateString( timestamp, [formattedDate, formattedTime] ) }
                  </Text>
                </div>
              </div>

              <div className='GiftCardBalanceChecker__giftCardTitle'>
                <Text
                  htmlTag={ giftCardTitle.htmlTag }
                  textStyle='eyebrowDefault'
                  textAlign='center'
                  color='neutral-700'
                >
                  { giftCardTitle.text }
                </Text>
              </div>
              <div className='GiftCardBalanceChecker__cardNumber__section'>
                <Icon
                  name={ giftCardIcon }
                  aria-hidden={ true }
                  size='cc'
                />
                <div className='GiftCardBalanceChecker__cardNumber'>
                  <Text
                    htmlTag='p'
                    textStyle='bodyDefault'
                    textAlign='center'
                  >
                    { cardNumber }
                  </Text>
                </div>
              </div>
              <div className='GiftCardBalanceChecker__checkAnotherCardAction'>
                <Button
                  type='submit'
                  fullWidth={ true }
                  secondary={ true }
                  label={ checkAnotherCardAction.label }
                  ariaLabel={ checkAnotherCardAction.label }
                  onClick={ () => setGiftCardView( 'input' ) }
                  action={ checkAnotherCardAction }
                />
              </div>
              <Markdown
                bodyStyle='body-2'
                cmsMarkdownContent={ helpText }
                alignment='center'
              />
            </div>
          }
          { giftCardView === 'unavailable' &&
            <div className='GiftCardBalanceChecker__content'>
              <div className='GiftCardBalanceChecker__checkBalanceTitle'>
                <Text
                  htmlTag={ unavailableHeadline?.htmlTag }
                  textStyle='title-xlg'
                  textAlign='center'
                >
                  { unavailableHeadline?.text }
                </Text>
              </div>
              <div className='GiftCardBalanceChecker__messageBlock'>
                <div className='GiftCardBalanceChecker__subHeadline'>
                  <Text
                    htmlTag='p'
                    textStyle='body-1'
                    textAlign='center'
                  >
                    { unavailableMessage }
                  </Text>
                </div>
                <Markdown
                  bodyStyle='body-1'
                  cmsMarkdownContent={ helpText }
                  alignment='center'
                />
              </div>
              <div className='GiftCardBalanceChecker__callSupportActionBtn'>
                <Link_Huge
                  action={ callGiftCardSupportAction }
                  likeButtonPrimary
                  className='pal-c-Button--fullWidth'
                >
                  { callGiftCardSupportAction?.label }
                </Link_Huge>
              </div>
              <div className='GiftCardBalanceChecker__continueShoppingActionBtn'>
                <Link_Huge
                  action={ continueShoppingAction }
                  likeButtonSecondary
                  className='pal-c-Button--fullWidth'
                >
                  { continueShoppingAction?.label }
                </Link_Huge>
              </div>
            </div>
          }
        </GridContainer>
      </div>
    </div>
  );
};

/**
 * Method to handle searchPanel close event
 * @param {String} data - Passing data as an argument
 * @param {object} data.action DXL action
 * @param {object} methods Methods
 * @param {function} methods.invokeMutation invokeMutation
 * @param {function} methods.showLoader showLoader
*/
export const composeOnSubmit = ( data, methods ) => ( values ) => {
  const { action } = data || {};
  const { invokeMutation, showLoader } = methods || {};
  const { graphql, customHeaders } = action || {};

  if( !values || !graphql || !invokeMutation || !showLoader ){
    return;
  }

  showLoader();

  invokeMutation( {
    graphql,
    customHeaders,
    variables: {
      moduleParams: {
        numberInput: { name: 'numberInput', value: values.numberInput.replace( /\s/g, '' ) },
        pinInput: { name: 'pinInput', value: values.pinInput }
      }
    }
  } );
};

/**
 * handleLoadingState function to hide loader
 * @method
 * @param {object} data Arguments
 * @param {object} methods Methods
 * @param {function} methods.hideLoader hideLoader
 * @returns {function} hideLoader
 */
export const handleLoadingState = ( data, methods ) => {
  const { loading } = data || {};
  const { hideLoader } = methods || {};
  if( !hideLoader || loading !== false ){
    return;
  }
  hideLoader();
};

/**
* A handler function for manually setting form fields
* @method getValidationSchema
* @param {Object} data - Contains input fields and their Amplience configurations
* @returns {Object} validationSchema - Returns Object containing Yup validations for each passed in field.
*/
export const getValidationSchema = ( data ) => {
  const { numberInput, pinInput } = handleEmptyObjects( data );

  addYupCustomRegex( { schemaType: Yup.string, name: 'numberInput', validations: numberInput?.validations } );
  addYupCustomRegex( { schemaType: Yup.string, name: 'pinInput', validations: pinInput?.validations } );

  Yup.addMethod( Yup.string, 'isRequiredField', isRequiredValidation );
  return Yup.object( {
    numberInput: numberInput?.validations?.[0] && Yup.string().isRequiredField( numberInput ).numberInput(),
    pinInput: pinInput?.validations?.[0] && Yup.string().isRequiredField( pinInput ).pinInput()
  } );
};

/**
 * Property type definitions
 * @typedef GiftCardBalanceChecker
 * @type {object}
 * @property {object}  background - desktop and mobile image object
 * @property {object}  input - gift card input fields
 * @property {string}  helpText - message to call support center
*/
export const propTypes = {
  background: PropTypes.shape( {
    desktopImage: PropTypes.object,
    mobileImage: PropTypes.object
  } ),
  input: PropTypes.shape( {
    headline: PropTypes.object,
    subHeadline: PropTypes.string,
    requiredText: PropTypes.string,
    numberInput: PropTypes.object,
    pinInput: PropTypes.object,
    checkBalanceAction: PropTypes.object
  } ),
  helpText: PropTypes.string,
  balance: PropTypes.shape( {
    headline: PropTypes.object,
    currentBalance: PropTypes.string,
    timestamp: PropTypes.string,
    giftCardTitle: PropTypes.object,
    iconAccessibilityLabel: PropTypes.string,
    giftCardIcon: PropTypes.string,
    cardNumber: PropTypes.string,
    checkAnotherCardAction: PropTypes.object,
    dataCaptureData: PropTypes.object
  } ),
  unavailable: PropTypes.shape( {
    headline: PropTypes.object,
    unavailableMessage: PropTypes.string,
    actionGroups: PropTypes.object
  } ),
  messages: PropTypes.array,
  selectedView: PropTypes.string,
  invokeMutation: PropTypes.func,
  loading: PropTypes.bool,
  dataCaptureEventList: PropTypes.array,
  root: PropTypes.object,
  rootMargin: PropTypes.string,
  threshold: PropTypes.oneOfType( [PropTypes.number, PropTypes.arrayOf( PropTypes.number )] )
};

GiftCardBalanceChecker.propTypes = propTypes;

export default GiftCardBalanceChecker;